import { useMemo, useRef } from 'react';
import { useLocation } from '@remix-run/react';
import { useToggle } from 'ahooks';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import type { DefaultElasticSearchResults } from '~/modules/professional/type';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel as ProjectTileViewModelRocco } from '@rocco/ui/tile-project/view-models/project';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { useProfessionalInitialRouteData } from '../../hook/use-professional-initial-route-data';

interface PaginatedProjectsProp extends DefaultElasticSearchResults {
    projectTilesRocco: TileViewModel<ProjectTileViewModelRocco>[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedProjects: React.FC<PaginatedProjectsProp> = ({
    pagination,
    projectTilesRocco,
    filterStates,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const { ShowOwnProjectsOnly } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const remixLocation = useLocation();
    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] =
        useToggle(!disableSort);
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const tiles = projectTilesRocco.map((tile) => {
        return (
            <div
                key={tile.model.id}
                className="transform-gpu transition-transform duration-300"
            >
                <CategoryPageTileWithAdminActions
                    tile={{
                        ...tile,
                        section: 'ProfessionalProjects',
                        disableDesignBoard:
                            isBMPreview || disableSaveToDesignBoard,
                        routeState: {
                            hideAd: true,
                        },
                    }}
                />
            </div>
        );
    });

    const ignoreFilterKeys = useMemo(() => {
        const keys = [...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS];
        // BC-2980: Professionals with the ShowOwnProjectsOnly flag should only
        // show projects they own.
        if (ShowOwnProjectsOnly) {
            keys.push('viewProjectsBy');
        }
        return keys;
    }, [ShowOwnProjectsOnly]);

    const category = useMatchesHandleData<SearchIndex>('category', 'project');

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <div>
            <div className="mx-12.5 hidden h-[1px] bg-black md:block qhd:px-0"></div>

            <div className="md:mt-16 md:pb-47.5">
                <CollapsiblePageLayout
                    sidePanel={
                        desktop && (
                            <div className="md:pt-15">
                                <CategoryFilterNav
                                    category="project"
                                    searchFilters={searchFilters}
                                    hideCategorySectionTitle={false}
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div className="md:h-15">
                        <SearchFilter
                            searchFilters={searchFilters}
                            filterDrawerOpen={filterDrawerOpen}
                            toggleFilterDrawer={toggleFilterDrawer}
                            disableSort={disableSort}
                            disableToggleFilter={true}
                        />
                    </div>
                    {searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <AppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                resetFilters={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...ignoreFilterKeys, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}
                    <div>
                        {!tiles.length ? (
                            <NoSearchResults resultsType={'projects'} />
                        ) : (
                            <>
                                <Grid
                                    ref={gridRef}
                                    colClasses={'grid-cols-1 lg:grid-cols-3'}
                                    rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                                >
                                    {tiles}
                                </Grid>

                                {pagination.totalPages > 1 && (
                                    <div className="pt-[6.5625rem] md:pt-20">
                                        <TilePagination
                                            currentPath={remixLocation}
                                            current={pagination.page}
                                            total={pagination.totalPages}
                                            urlBuilder={buildPaginationUrl}
                                            visibleCount={desktop ? 4 : 3}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CollapsiblePageLayout>
            </div>
        </div>
    );
};
